export type TimeOfDay = 'day' | 'twilight' | 'night';
export type WeatherKind = 'clear' | 'breezy' | 'rain' | 'storm';

export interface EnvironmentSnapshot {
	cycle: number;
	timeOfDay: TimeOfDay;
	timeLabel: string;
	weather: WeatherKind;
	weatherLabel: string;
	weatherIndex: number;
	weatherBlend: number;
	windParticleScale: number;
	rainDropCount: number;
	rainLineWidth: number;
	cloudAlpha: number;
	skyIsDaylit: boolean;
	ambientLight: number;
	lightAngle: number;
	hazeStrength: number;
	hazeColor: string;
	atmosphereTint: string;
	parallaxColor: string;
	parallaxAlpha: number;
	terrainHighlight: string;
	terrainShadow: string;
	terrainShadeAlpha: number;
	tankShadowAlpha: number;
	tankLightTint: number;
}

const WEATHER_LABELS = ['Clear', 'Breezy', 'Rain', 'Storm'] as const;
const WEATHER_KINDS: WeatherKind[] = ['clear', 'breezy', 'rain', 'storm'];

interface WeatherVisuals {
	windParticleScale: number;
	rainDropCount: number;
	rainLineWidth: number;
	cloudAlpha: number;
}

interface LightingFields {
	ambientLight: number;
	lightAngle: number;
	hazeStrength: number;
	hazeColor: string;
	atmosphereTint: string;
	parallaxColor: string;
	parallaxAlpha: number;
	terrainHighlight: string;
	terrainShadow: string;
	terrainShadeAlpha: number;
	tankShadowAlpha: number;
	tankLightTint: number;
}

export function computeEnvironment(seed: number, round: number, animTime = 0): EnvironmentSnapshot {
	// Full day/night loop ~90s of animTime; each round also shifts the starting phase.
	const cycle = ((seed % 100) / 100 + (round - 1) * 0.22 + animTime / 90) % 1;
	// Continuous weather phase so rain/clouds/haze lerp instead of hard-cutting every quarter-day.
	const weatherContinuous = mod(((seed + round * 31) % 4) + cycle * 4, 4);
	const weatherIndexA = Math.floor(weatherContinuous) % 4;
	const weatherIndexB = (weatherIndexA + 1) % 4;
	const weatherBlend = smoothstep(weatherContinuous - Math.floor(weatherContinuous));
	const weatherA = WEATHER_KINDS[weatherIndexA];
	const weatherB = WEATHER_KINDS[weatherIndexB];
	const weatherIndex = weatherBlend < 0.5 ? weatherIndexA : weatherIndexB;
	const weather = WEATHER_KINDS[weatherIndex];
	const visuals = lerpWeatherVisuals(weatherVisuals(weatherA), weatherVisuals(weatherB), weatherBlend);

	const timeOfDay = resolveTimeOfDay(cycle);
	const skyIsDaylit = timeOfDay !== 'night';
	const lighting = resolveLightingSmooth(cycle, weatherA, weatherB, weatherBlend);

	return {
		cycle,
		timeOfDay,
		timeLabel: timeOfDay === 'day' ? 'Day' : timeOfDay === 'twilight' ? 'Twilight' : 'Night',
		weather,
		weatherLabel: WEATHER_LABELS[weatherIndex],
		weatherIndex,
		weatherBlend,
		...visuals,
		skyIsDaylit,
		...lighting,
	};
}

function weatherVisuals(weather: WeatherKind): WeatherVisuals {
	return {
		windParticleScale: weather === 'clear' ? 0.85 : weather === 'breezy' ? 1.25 : weather === 'rain' ? 1.1 : 1.4,
		rainDropCount: weather === 'storm' ? 110 : weather === 'rain' ? 64 : 0,
		rainLineWidth: weather === 'storm' ? 1.7 : weather === 'rain' ? 1.15 : 0.8,
		cloudAlpha: weather === 'storm' ? 0.88 : weather === 'rain' ? 0.74 : weather === 'breezy' ? 0.58 : 0.42,
	};
}

function lerpWeatherVisuals(a: WeatherVisuals, b: WeatherVisuals, t: number): WeatherVisuals {
	return {
		windParticleScale: lerp(a.windParticleScale, b.windParticleScale, t),
		rainDropCount: Math.round(lerp(a.rainDropCount, b.rainDropCount, t)),
		rainLineWidth: lerp(a.rainLineWidth, b.rainLineWidth, t),
		cloudAlpha: lerp(a.cloudAlpha, b.cloudAlpha, t),
	};
}

function resolveLightingSmooth(
	cycle: number,
	weatherA: WeatherKind,
	weatherB: WeatherKind,
	weatherBlend: number,
): LightingFields {
	const tod = resolveTimeOfDay(cycle);
	const weatherMixed = lerpLighting(
		resolveLighting(tod, weatherA, cycle),
		resolveLighting(tod, weatherB, cycle),
		weatherBlend,
	);

	// Soften day↔twilight↔night seams with a continuous day amount (avoids hard sky cuts).
	const dayAmount = softDayAmount(cycle);
	const nightLit = lerpLighting(
		resolveLighting('night', weatherA, cycle),
		resolveLighting('night', weatherB, cycle),
		weatherBlend,
	);
	const dayLit = lerpLighting(
		resolveLighting('day', weatherA, cycle),
		resolveLighting('day', weatherB, cycle),
		weatherBlend,
	);
	const twilightLit = lerpLighting(
		resolveLighting('twilight', weatherA, cycle),
		resolveLighting('twilight', weatherB, cycle),
		weatherBlend,
	);
	if (dayAmount >= 0.55) {
		return lerpLighting(twilightLit, dayLit, (dayAmount - 0.55) / 0.45);
	}
	if (dayAmount <= 0.2) {
		return lerpLighting(nightLit, twilightLit, dayAmount / 0.2);
	}
	return lerpLighting(weatherMixed, twilightLit, 0.35);
}

/** 0 = deep night, ~0.45 = twilight, 1 = midday. */
function softDayAmount(cycle: number): number {
	if (cycle >= 0.28 && cycle < 0.68) {
		const into = smoothstep((cycle - 0.28) / 0.08);
		const out = 1 - smoothstep((cycle - 0.6) / 0.08);
		return 0.55 + 0.45 * Math.min(into, out);
	}
	if (cycle >= 0.18 && cycle < 0.28) {
		return 0.2 + 0.35 * smoothstep((cycle - 0.18) / 0.1);
	}
	if (cycle >= 0.68 && cycle < 0.78) {
		return 0.55 * (1 - smoothstep((cycle - 0.68) / 0.1));
	}
	return 0;
}

function resolveLighting(
	timeOfDay: TimeOfDay,
	weather: WeatherKind,
	cycle: number,
): LightingFields {
	const weatherHazeBoost =
		weather === 'clear' ? 0 : weather === 'breezy' ? 0.06 : weather === 'rain' ? 0.14 : 0.22;
	const weatherDim =
		weather === 'clear' ? 0 : weather === 'breezy' ? 0.04 : weather === 'rain' ? 0.12 : 0.2;

	if (timeOfDay === 'day') {
		const sunBias = Math.sin(cycle * Math.PI * 2) * 0.15;
		return {
			ambientLight: 0.92 - weatherDim,
			lightAngle: 0.72 + sunBias,
			hazeStrength: 0.18 + weatherHazeBoost,
			hazeColor: weather === 'storm' ? 'rgba(140,155,175,0.38)' : 'rgba(255,235,200,0.28)',
			atmosphereTint: weather === 'storm' ? 'rgba(180,190,210,0.12)' : 'rgba(255,248,220,0.08)',
			parallaxColor: weather === 'storm' ? '#3a4558' : '#254832',
			parallaxAlpha: weather === 'storm' ? 0.62 : 0.82,
			terrainHighlight: 'rgba(200,230,140,0.48)',
			terrainShadow: 'rgba(8,14,6,0.38)',
			terrainShadeAlpha: 0.1 + weatherDim * 0.04,
			tankShadowAlpha: 0.38 - weatherDim * 0.45,
			tankLightTint: 0.22 - weatherDim * 0.3,
		};
	}
	if (timeOfDay === 'twilight') {
		return {
			ambientLight: 0.62 - weatherDim,
			lightAngle: 0.35,
			hazeStrength: 0.28 + weatherHazeBoost,
			hazeColor: weather === 'storm' ? 'rgba(90,80,110,0.42)' : 'rgba(255,180,140,0.32)',
			atmosphereTint: 'rgba(255,160,120,0.14)',
			parallaxColor: weather === 'storm' ? '#2a2438' : '#3d2a38',
			parallaxAlpha: 0.68,
			terrainHighlight: 'rgba(255,200,150,0.32)',
			terrainShadow: 'rgba(12,8,18,0.48)',
			terrainShadeAlpha: 0.14,
			tankShadowAlpha: 0.38,
			tankLightTint: 0.12,
		};
	}
	return {
		ambientLight: 0.38 - weatherDim,
		lightAngle: 0.85,
		hazeStrength: 0.22 + weatherHazeBoost,
		hazeColor: weather === 'storm' ? 'rgba(40,50,80,0.48)' : 'rgba(60,70,110,0.32)',
		atmosphereTint: 'rgba(100,120,180,0.16)',
		parallaxColor: weather === 'storm' ? '#121828' : '#141c2c',
		parallaxAlpha: 0.74,
		terrainHighlight: 'rgba(160,180,220,0.22)',
		terrainShadow: 'rgba(4,6,14,0.55)',
		terrainShadeAlpha: 0.18,
		tankShadowAlpha: 0.45,
		tankLightTint: 0.08,
	};
}

function lerpLighting(a: LightingFields, b: LightingFields, t: number): LightingFields {
	return {
		ambientLight: lerp(a.ambientLight, b.ambientLight, t),
		lightAngle: lerp(a.lightAngle, b.lightAngle, t),
		hazeStrength: lerp(a.hazeStrength, b.hazeStrength, t),
		hazeColor: t < 0.5 ? a.hazeColor : b.hazeColor,
		atmosphereTint: t < 0.5 ? a.atmosphereTint : b.atmosphereTint,
		parallaxColor: t < 0.5 ? a.parallaxColor : b.parallaxColor,
		parallaxAlpha: lerp(a.parallaxAlpha, b.parallaxAlpha, t),
		terrainHighlight: t < 0.5 ? a.terrainHighlight : b.terrainHighlight,
		terrainShadow: t < 0.5 ? a.terrainShadow : b.terrainShadow,
		terrainShadeAlpha: lerp(a.terrainShadeAlpha, b.terrainShadeAlpha, t),
		tankShadowAlpha: lerp(a.tankShadowAlpha, b.tankShadowAlpha, t),
		tankLightTint: lerp(a.tankLightTint, b.tankLightTint, t),
	};
}

export function colorWithAlpha(rgba: string, alpha: number): string {
	return rgba.replace(/,\s*[\d.]+\)$/, `, ${alpha})`);
}

function resolveTimeOfDay(cycle: number): TimeOfDay {
	if (cycle >= 0.28 && cycle < 0.68) {
		return 'day';
	}
	if ((cycle >= 0.18 && cycle < 0.28) || (cycle >= 0.68 && cycle < 0.78)) {
		return 'twilight';
	}
	return 'night';
}

function lerp(a: number, b: number, t: number): number {
	return a + (b - a) * t;
}

function smoothstep(t: number): number {
	const x = Math.max(0, Math.min(1, t));
	return x * x * (3 - 2 * x);
}

function mod(n: number, m: number): number {
	return ((n % m) + m) % m;
}
